import { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Server,
  Copy,
  Check,
  Eye,
  EyeOff,
  Activity,
  Cpu,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Database,
  FileText,
  Mic,
  AlertTriangle,
  Gauge,
  Users,
  TrendingUp,
  Timer,
  MemoryStick,
  Hash,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Ban,
  Fingerprint,
  Network,
  Upload,
  Play,
  Square,
  Layers,
  Box,
  Image,
  Info,
  BookOpen,
  FolderTree,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import {
  TritonInstanceDetails,
  TritonModelInfo,
  mockTritonInstanceDetails,
} from '@/mock/gpu-instance';
import { cn } from '@/lib/utils';

interface TritonDetailPanelProps {
  taskId: string;
}

const getModelIcon = (name: string, backend: string) => {
  if (name.includes('embedding') || name.includes('bge') || name.includes('bert')) {
    return <Database className="h-4 w-4 text-blue-500" />;
  }
  if (name.includes('yolo') || name.includes('detection') || name.includes('resnet') || name.includes('classifier')) {
    return <Image className="h-4 w-4 text-purple-500" />;
  }
  if (name.includes('whisper') || name.includes('speech')) {
    return <Mic className="h-4 w-4 text-cyan-500" />;
  }
  if (backend === 'tensorrt') {
    return <Cpu className="h-4 w-4 text-green-500" />;
  }
  return <Box className="h-4 w-4 text-gray-500" />;
};

const getStatusIcon = (status: TritonModelInfo['status']) => {
  switch (status) {
    case 'ready':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'loading':
      return <Loader2 className="h-4 w-4 text-yellow-500 animate-spin" />;
    case 'unavailable':
      return <AlertCircle className="h-4 w-4 text-gray-500" />;
    case 'error':
      return <AlertCircle className="h-4 w-4 text-red-500" />;
  }
};

const getBackendColor = (backend: string) => {
  switch (backend) {
    case 'tensorrt':
      return 'bg-green-500/10 text-green-600 border-green-500/30';
    case 'onnxruntime':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/30';
    case 'pytorch':
      return 'bg-orange-500/10 text-orange-600 border-orange-500/30';
    case 'python':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
    default:
      return 'bg-gray-500/10 text-gray-600 border-gray-500/30';
  }
};

const TritonDetailPanel = ({ taskId }: TritonDetailPanelProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  const details: TritonInstanceDetails = useMemo(
    () => mockTritonInstanceDetails(taskId),
    [taskId]
  );

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 16) return '••••••••••••••••';
    return `${key.slice(0, 10)}${'•'.repeat(16)}${key.slice(-4)}`;
  };

  const getStatusColor = (status: TritonInstanceDetails['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500/10 text-green-600 border-green-500/30';
      case 'degraded':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
      case 'offline':
        return 'bg-red-500/10 text-red-600 border-red-500/30';
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const readyModels = details.models.filter((m) => m.status === 'ready').length;
  const { productionMetrics, botPrevention, modelManagement } = details;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-blue-500/50 mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Server className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Triton Inference Server
                  <Badge variant="outline" className={getStatusColor(details.status)}>
                    {details.status}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Multi-framework inference server for production AI
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono">
                v{details.serverVersion}
              </Badge>
              <Badge variant="outline" className="bg-green-500/10 text-green-600">
                {readyModels}/{details.models.length} Models Ready
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Production Dashboard */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Gauge className="h-5 w-5" />
            Production Dashboard
          </CardTitle>
          <CardDescription>Real-time inference metrics</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Primary Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Activity className="h-4 w-4" />
                <span className="text-xs">Requests/sec</span>
              </div>
              <div className="text-2xl font-bold">{productionMetrics.requestsPerSecond}</div>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-xs">Avg Latency</span>
              </div>
              <div className="text-2xl font-bold">{productionMetrics.avgLatencyMs}ms</div>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs">Error Rate</span>
              </div>
              <div className="text-2xl font-bold">{productionMetrics.errorRate}%</div>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                <span className="text-xs">Active Connections</span>
              </div>
              <div className="text-2xl font-bold">{productionMetrics.activeConnections}</div>
            </div>
          </div>

          {/* Latency Percentiles */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-muted/30 rounded-lg text-center">
              <div className="text-xs text-muted-foreground mb-1">P50 Latency</div>
              <div className="text-lg font-semibold">{productionMetrics.p50LatencyMs}ms</div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-center">
              <div className="text-xs text-muted-foreground mb-1">P95 Latency</div>
              <div className="text-lg font-semibold">{productionMetrics.p95LatencyMs}ms</div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-center">
              <div className="text-xs text-muted-foreground mb-1">P99 Latency</div>
              <div className="text-lg font-semibold">{productionMetrics.p99LatencyMs}ms</div>
            </div>
          </div>

          {/* GPU & System Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Cpu className="h-3 w-3" />
                <span className="text-xs">GPU Utilization</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">{productionMetrics.gpuUtilization}%</span>
                <Progress value={productionMetrics.gpuUtilization} className="h-2 flex-1" />
              </div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <MemoryStick className="h-3 w-3" />
                <span className="text-xs">GPU Memory</span>
              </div>
              <div className="text-lg font-semibold">
                {productionMetrics.gpuMemoryUsed}/{productionMetrics.gpuMemoryTotal}GB
              </div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Layers className="h-3 w-3" />
                <span className="text-xs">Queued Requests</span>
              </div>
              <div className="text-lg font-semibold">{productionMetrics.queuedRequests}</div>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Timer className="h-3 w-3" />
                <span className="text-xs">Uptime</span>
              </div>
              <div className="text-lg font-semibold">{productionMetrics.uptime}</div>
            </div>
          </div>

          {/* 24h Stats */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">24h Total Inferences</span>
              </div>
              <span className="text-lg font-bold">{formatNumber(productionMetrics.totalInferences24h)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bot Prevention */}
      <Card id="bot-prevention">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Bot Prevention
              </CardTitle>
              <CardDescription>Rate limiting and Proof of Work protection</CardDescription>
            </div>
            <Badge
              variant="outline"
              className={cn(
                "text-xs",
                botPrevention.enabled
                  ? "text-green-600 border-green-600/30 bg-green-500/10"
                  : "text-gray-500 border-gray-500/30"
              )}
            >
              {botPrevention.enabled ? (
                <><ShieldCheck className="h-3 w-3 mr-1" /> Active</>
              ) : (
                <><ShieldAlert className="h-3 w-3 mr-1" /> Disabled</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Protection Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-red-500/5 rounded-lg border border-red-500/20">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <Ban className="h-3 w-3" />
                <span className="text-xs">Blocked (24h)</span>
              </div>
              <div className="text-lg font-bold">{formatNumber(botPrevention.stats.blockedRequests24h)}</div>
            </div>
            <div className="p-3 bg-orange-500/5 rounded-lg border border-orange-500/20">
              <div className="flex items-center gap-2 text-orange-600 mb-1">
                <AlertTriangle className="h-3 w-3" />
                <span className="text-xs">Rate Limited (24h)</span>
              </div>
              <div className="text-lg font-bold">{formatNumber(botPrevention.stats.rateLimitedRequests24h)}</div>
            </div>
            <div className="p-3 bg-green-500/5 rounded-lg border border-green-500/20">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <Fingerprint className="h-3 w-3" />
                <span className="text-xs">PoW Solved (24h)</span>
              </div>
              <div className="text-lg font-bold">{formatNumber(botPrevention.stats.powChallengesSolved24h)}</div>
            </div>
            <div className="p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <Timer className="h-3 w-3" />
                <span className="text-xs">Avg PoW Time</span>
              </div>
              <div className="text-lg font-bold">{botPrevention.stats.avgPowSolveTimeMs}ms</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Rate Limiting */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Network className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">Rate Limiting by IP</span>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs",
                    botPrevention.rateLimit.enabled
                      ? "text-green-600 border-green-600/30"
                      : "text-gray-500 border-gray-500/30"
                  )}
                >
                  {botPrevention.rateLimit.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Per Minute</span>
                  <span className="font-mono">{botPrevention.rateLimit.requestsPerMinute}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Per Hour</span>
                  <span className="font-mono">{botPrevention.rateLimit.requestsPerHour}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Burst Limit</span>
                  <span className="font-mono">{botPrevention.rateLimit.burstLimit}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Blocked IPs</span>
                  <span className="font-mono">{botPrevention.rateLimit.blockedIPs.length}</span>
                </div>
              </div>
            </div>

            {/* PoW Stats */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Fingerprint className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">Proof of Work Stats</span>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs",
                    botPrevention.proofOfWork.enabled
                      ? "text-green-600 border-green-600/30"
                      : "text-gray-500 border-gray-500/30"
                  )}
                >
                  {botPrevention.proofOfWork.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Solved (24h)</span>
                  <span className="font-mono">{formatNumber(botPrevention.stats.powChallengesSolved24h)}</span>
                </div>
                <div className="flex justify-between p-2 bg-background rounded">
                  <span className="text-muted-foreground">Avg Solve Time</span>
                  <span className="font-mono">{botPrevention.stats.avgPowSolveTimeMs}ms</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                See PoW Protected Endpoint section below for integration details.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PoW Protected Endpoint */}
      {botPrevention.proofOfWork.enabled && (
        <Card className="border-purple-500/30 mt-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Fingerprint className="h-5 w-5 text-purple-500" />
                  PoW Protected Endpoint
                </CardTitle>
                <CardDescription>
                  Requires solving a SHA256 challenge before making inference requests
                </CardDescription>
              </div>
              <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
                Challenge Required
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* PoW Configuration */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Algorithm</div>
                <div className="font-mono text-sm uppercase">{botPrevention.proofOfWork.algorithm}</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Difficulty</div>
                <Badge variant="secondary" className="text-xs capitalize">
                  {botPrevention.proofOfWork.difficulty}
                </Badge>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Challenge Validity</div>
                <div className="font-mono text-sm">{botPrevention.proofOfWork.validitySeconds}s</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Skip Auth Users</div>
                <div className="font-mono text-sm">{botPrevention.proofOfWork.exemptAuthenticatedUsers ? 'Yes' : 'No'}</div>
              </div>
            </div>

            {/* Challenge Flow */}
            <div className="space-y-3">
              <div className="text-sm font-medium">Integration Flow</div>

              {/* Step 1 */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md bg-blue-600 text-white text-xs font-semibold">Step 1</span>
                  <span className="text-sm font-medium">Request Challenge</span>
                </div>
                <div className="relative">
                  <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`GET ${botPrevention.proofOfWork.challengeEndpoint}

Response:
{
  "challenge": "a8f3e2...",
  "difficulty": 4,
  "expires_at": 1704067200
}`}
                  </pre>
                </div>
              </div>

              {/* Step 2 */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md bg-amber-500 text-white text-xs font-semibold">Step 2</span>
                  <span className="text-sm font-medium">Solve Challenge (Client-side)</span>
                </div>
                <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`// Find nonce where SHA256(challenge + nonce) starts with N zeros
let nonce = 0;
while (true) {
  const hash = SHA256(challenge + nonce);
  if (hash.startsWith("0".repeat(difficulty))) break;
  nonce++;
}`}
                </pre>
              </div>

              {/* Step 3 */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md bg-emerald-600 text-white text-xs font-semibold">Step 3</span>
                  <span className="text-sm font-medium">Make Inference Request with Solution</span>
                </div>
                <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`POST ${botPrevention.proofOfWork.protectedEndpoint.replace('{MODEL_NAME}', 'bge-large-en-v1.5')}
Headers:
  X-PoW-Challenge: <challenge>
  X-PoW-Nonce: <nonce>
  Authorization: Bearer ${details.apiKey}

Body:
{
  "inputs": [{"name": "text", "data": ["Hello"], "datatype": "BYTES"}]
}`}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Model Repository */}
      <Card id="model-repository">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="h-5 w-5" />
                Model Repository
              </CardTitle>
              <CardDescription>Deployed models and their inference endpoints</CardDescription>
            </div>
            <Badge variant="outline" className="font-mono text-xs">
              {modelManagement.controlMode.toUpperCase()} mode
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model</TableHead>
                <TableHead>Backend</TableHead>
                <TableHead>Instances</TableHead>
                <TableHead>Batching</TableHead>
                <TableHead>Inferences</TableHead>
                <TableHead>Latency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {details.models.map((model) => (
                <TableRow key={model.name}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getModelIcon(model.name, model.backend)}
                      <div>
                        <div className="font-medium">{model.name}</div>
                        <div className="text-xs text-muted-foreground">v{model.version}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("font-mono text-xs", getBackendColor(model.backend))}>
                      {model.backend}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{model.instances}</TableCell>
                  <TableCell>
                    {model.dynamicBatching ? (
                      <Badge variant="outline" className="text-xs text-green-600 border-green-600/30">
                        max {model.maxBatchSize}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Off</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {formatNumber(model.metrics.inferenceCount)}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <span className="font-mono">{model.metrics.avgLatencyMs}ms</span>
                      <span className="text-xs text-muted-foreground ml-1">avg</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(model.status)}
                      <span className="text-sm capitalize">{model.status}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy(model.endpoint, model.name)}
                    >
                      {copiedField === model.name ? (
                        <Check className="size-4" />
                      ) : (
                        <Copy className="size-4" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Model Management */}
      <Card id="model-management">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Model Management
          </CardTitle>
          <CardDescription>Load, unload, and manage models at runtime</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left: Guide Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              <div className="p-4 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
                <div className="flex items-center gap-2 text-sm font-semibold mb-3">
                  <BookOpen className="h-4 w-4 text-blue-500" />
                  How to Manage Models
                </div>

                <div className="space-y-3">
                  {/* Control Mode */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs font-medium">
                      <Settings2 className="h-3 w-3 text-muted-foreground" />
                      Control Mode
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Currently using <span className="font-mono text-blue-500">{modelManagement.controlMode.toUpperCase()}</span> mode.
                      Models are loaded/unloaded only via API calls.
                    </p>
                  </div>

                  {/* Hot Loading */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs font-medium">
                      <RefreshCw className="h-3 w-3 text-muted-foreground" />
                      Hot Loading
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      New models load in background without interrupting existing inference requests.
                    </p>
                  </div>

                  {/* Repository Structure */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs font-medium">
                      <FolderTree className="h-3 w-3 text-muted-foreground" />
                      Repository Structure
                    </div>
                    <div className="bg-[#1a1b26] rounded p-2 text-[10px] font-mono text-gray-400">
                      <div>{modelManagement.repositoryPath}/</div>
                      <div className="pl-2">model_name/</div>
                      <div className="pl-4">config.pbtxt</div>
                      <div className="pl-4">1/</div>
                      <div className="pl-6">model.onnx</div>
                    </div>
                  </div>

                  {/* Supported Backends */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs font-medium">
                      <Cpu className="h-3 w-3 text-muted-foreground" />
                      Supported Backends
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {['tensorrt', 'onnx', 'pytorch', 'python', 'openvino'].map((backend) => (
                        <Badge key={backend} variant="outline" className="text-[10px] px-1.5 py-0">
                          {backend}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="p-3 bg-amber-500/5 rounded-lg border border-amber-500/20">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium text-amber-600">Tip:</span> Use version directories (1/, 2/, etc.) to deploy multiple model versions and enable A/B testing.
                  </div>
                </div>
              </div>
            </div>

            {/* Right: API Endpoints */}
            <div className="lg:col-span-2 space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="p-3 bg-muted/30 rounded-lg space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Database className="h-4 w-4" />
                    List Models
                  </div>
                  <div className="bg-[#1a1b26] text-gray-300 p-2 rounded text-xs font-mono overflow-hidden">
                    <div className="text-green-400">POST</div>
                    <div className="truncate">{modelManagement.endpoints.index}</div>
                  </div>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Play className="h-4 w-4 text-green-500" />
                    Load Model
                  </div>
                  <div className="bg-[#1a1b26] text-gray-300 p-2 rounded text-xs font-mono overflow-hidden">
                    <div className="text-green-400">POST</div>
                    <div className="truncate">{modelManagement.endpoints.load}</div>
                  </div>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Square className="h-4 w-4 text-red-500" />
                    Unload Model
                  </div>
                  <div className="bg-[#1a1b26] text-gray-300 p-2 rounded text-xs font-mono overflow-hidden">
                    <div className="text-green-400">POST</div>
                    <div className="truncate">{modelManagement.endpoints.unload}</div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                <div className="text-sm font-medium mb-2">Upload New Model via API</div>
                <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`POST ${modelManagement.endpoints.load.replace('{MODEL_NAME}', 'my-new-model')}
Content-Type: application/json

{
  "parameters": {
    "config": "{\\"backend\\":\\"onnxruntime\\",\\"max_batch_size\\":8}",
    "file:1/model.onnx": "<base64-encoded-model-file>"
  }
}`}
                </pre>
              </div>

              <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/20">
                <div className="text-sm font-medium mb-2">Load Existing Model from Repository</div>
                <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`POST ${modelManagement.endpoints.load.replace('{MODEL_NAME}', 'my-model')}
Content-Type: application/json

{}  // Empty body loads from repository path`}
                </pre>
              </div>

              <div className="p-4 bg-red-500/5 rounded-lg border border-red-500/20">
                <div className="text-sm font-medium mb-2">Unload Model (Free Resources)</div>
                <pre className="bg-[#1a1b26] text-gray-300 p-3 rounded-lg text-xs font-mono overflow-x-auto">
{`POST ${modelManagement.endpoints.unload.replace('{MODEL_NAME}', 'my-model')}
Content-Type: application/json

{"parameters": {"unload_dependents": true}}`}
                </pre>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Access */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Server className="h-5 w-5" />
            API Access
          </CardTitle>
          <CardDescription>Triton Inference Protocol (HTTP/gRPC)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Base Endpoint</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-sm font-mono whitespace-pre-wrap break-all pr-12">
                {details.baseEndpoint}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-8"
                onClick={() => handleCopy(details.baseEndpoint, 'endpoint')}
              >
                {copiedField === 'endpoint' ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">API Key</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-sm font-mono whitespace-pre-wrap break-all pr-20">
                {showApiKey ? details.apiKey : maskApiKey(details.apiKey)}
              </pre>
              <div className="absolute top-1 right-1 flex gap-1">
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setShowApiKey(!showApiKey)}>
                  {showApiKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => handleCopy(details.apiKey, 'apiKey')}>
                  {copiedField === 'apiKey' ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Example: Embedding Request</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-xs font-mono whitespace-pre-wrap break-all pr-12 overflow-x-auto">
{`curl -X POST ${details.models[0]?.endpoint || ''} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${details.apiKey}" \\
  -d '{
    "inputs": [
      {"name": "text", "data": ["Hello world"], "datatype": "BYTES"}
    ]
  }'`}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-8"
                onClick={() => handleCopy('curl example', 'curl')}
              >
                {copiedField === 'curl' ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> This is simulated data. In production,
          the model repository and endpoints will be fully functional.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default TritonDetailPanel;
